import * as i0 from '@angular/core';
import { inject, ElementRef, Component, HostBinding, ViewChild, HostListener, EventEmitter, ViewContainerRef, Renderer2, NgZone, ChangeDetectorRef, PLATFORM_ID, Directive, Input, Output, NgModule } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { resolveReference, responsivelyCheckDirection, getContentPositionString } from '@simpl/element-ng/common';
import { ResizeObserverService } from '@simpl/element-ng/resize-observer';

class SiDropdownContainerComponent {
    parent;
    elementTransform = '';
    hide = false;
    get isDropup() {
        return this.parent?.isDropup ?? false;
    }
    get isDropstart() {
        return this.parent?.isDropstart ?? false;
    }
    get isDropend() {
        return this.parent?.isDropend ?? false;
    }
    content = inject(ElementRef).nativeElement;
    menu;
    ngOnInit() {
        // Set a property of the HTML element for others to follow the chain without having to use the service.
        this.content.dropdownParentElement = this.parent.hostElement;
        this.addMenus();
    }
    ngOnDestroy() {
        this.removeMenus();
    }
    onKeyboardEvent(event) {
        if (this.parent) {
            const newEvent = new KeyboardEvent(event.type, { ...event, cancelable: true });
            event.stopPropagation();
            Object.defineProperty(newEvent, 'target', { writable: false, value: event.target });
            this.parent.hostElement.dispatchEvent(newEvent);
            if (event.defaultPrevented) {
                newEvent.preventDefault();
            }
        }
    }
    onFocusEvent(event) {
        if (this.parent) {
            const newEvent = new FocusEvent(event.type, { ...event, cancelable: true });
            event.stopPropagation();
            Object.defineProperty(newEvent, 'target', { writable: false, value: event.target });
            this.parent.hostElement.dispatchEvent(newEvent);
            if (event.defaultPrevented) {
                newEvent.preventDefault();
            }
        }
    }
    onMouseEvent(event) {
        if (this.parent) {
            const newEvent = new MouseEvent(event.type, { ...event, cancelable: true });
            event.stopPropagation();
            Object.defineProperty(newEvent, 'target', { writable: false, value: event.target });
            this.parent.hostElement.dispatchEvent(newEvent);
            if (event.defaultPrevented) {
                newEvent.preventDefault();
            }
        }
    }
    removeMenus() {
        if (this.parent) {
            this.parent.menus.forEach(menuContent => menuContent.parentElement?.appendChild(menuContent.content));
        }
    }
    addMenus() {
        if (this.parent) {
            this.parent.menus.forEach(menuContent => this.menu.nativeElement.appendChild(menuContent.content));
        }
    }
    setParent(parent) {
        this.parent = parent;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiDropdownContainerComponent, deps: [], target: i0.ɵɵFactoryTarget.Component });
    static ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "16.1.5", type: SiDropdownContainerComponent, selector: "si-dropdown-container", host: { listeners: { "keydown": "onKeyboardEvent($event)", "keyup": "onKeyboardEvent($event)", "keypress": "onKeyboardEvent($event)", "focus": "onFocusEvent($event)", "blur": "onFocusEvent($event)", "focusin": "onFocusEvent($event)", "focusout": "onFocusEvent($event)", "mousedown": "onMouseEvent($event)", "mouseup": "onMouseEvent($event)", "click": "onMouseEvent($event)" }, properties: { "class.hidden": "this.hide" } }, viewQueries: [{ propertyName: "menu", first: true, predicate: ["menu"], descendants: true, read: ElementRef, static: true }], ngImport: i0, template: "<div\n  #menu\n  class=\"dropdown\"\n  [style.transform]=\"elementTransform\"\n  [class.dropup]=\"isDropup\"\n  [class.dropstart]=\"isDropstart\"\n  [class.dropend]=\"isDropend\"\n></div>\n", styles: [":host.hidden{visibility:hidden}.dropdown{position:absolute;top:0;left:0;block-size:auto;z-index:1065}\n"] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiDropdownContainerComponent, decorators: [{
            type: Component,
            args: [{ selector: 'si-dropdown-container', template: "<div\n  #menu\n  class=\"dropdown\"\n  [style.transform]=\"elementTransform\"\n  [class.dropup]=\"isDropup\"\n  [class.dropstart]=\"isDropstart\"\n  [class.dropend]=\"isDropend\"\n></div>\n", styles: [":host.hidden{visibility:hidden}.dropdown{position:absolute;top:0;left:0;block-size:auto;z-index:1065}\n"] }]
        }], propDecorators: { hide: [{
                type: HostBinding,
                args: ['class.hidden']
            }], menu: [{
                type: ViewChild,
                args: ['menu', { read: ElementRef, static: true }]
            }], onKeyboardEvent: [{
                type: HostListener,
                args: ['keydown', ['$event']]
            }, {
                type: HostListener,
                args: ['keyup', ['$event']]
            }, {
                type: HostListener,
                args: ['keypress', ['$event']]
            }], onFocusEvent: [{
                type: HostListener,
                args: ['focus', ['$event']]
            }, {
                type: HostListener,
                args: ['blur', ['$event']]
            }, {
                type: HostListener,
                args: ['focusin', ['$event']]
            }, {
                type: HostListener,
                args: ['focusout', ['$event']]
            }], onMouseEvent: [{
                type: HostListener,
                args: ['mousedown', ['$event']]
            }, {
                type: HostListener,
                args: ['mouseup', ['$event']]
            }, {
                type: HostListener,
                args: ['click', ['$event']]
            }] } });

const RESIZE_THROTTLE_TIMEOUT = 100;
const DEPRECATED_WARNING_PREFIX = '@simpl/element-ng - SiDropdownDirective: ';
class SiDropdownDirective {
    /**
     * Disables the dropdown so it cannot be opened.
     */
    dropdownIsDisabled = false;
    /**
     * Opens the dropdown on changes, can be changed at any time.
     * To open it only on first load use {@link dropdownOpenOnLoad}
     */
    dropdownIsOpen = false;
    /**
     * Opens the dropdown when it first loads, to open it later use {@link dropdownIsOpen}.
     */
    dropdownOpenOnLoad = false;
    /**
     * Specifies whether the dropdown should be closed on selection of a dropdown item.
     */
    dropdownCloseOnSelect = true;
    /**
     * Specifies whether the dropdown should be closed on click outside.
     */
    dropdownCloseOnClickOut = true;
    /**
     * Specifies whether the dropdown should be closed on escape.
     */
    dropdownCloseOnEsc = true;
    _dropdownDirection = 'down';
    /**
     * Specifies the direction of the dropdown, can be `down`, `up`, `start`, `end`.
     * Per default it opens downwards.
     * @defaultref {@link _dropdownDirection}
     */
    set dropdownDirection(direction) {
        this._dropdownDirection = direction;
    }
    get dropdownDirection() {
        return this._dropdownDirection;
    }
    _dropdownAlign = 'start';
    /**
     * Aligns the dropdown to a side, only works if the direction is not `start` or `end`.
     * Can be `start`, `center` and `end`, per default it aligns to the start.
     * @defaultref {@link _dropdownAlign}
     */
    set dropdownAlign(align) {
        const deprecatedWarning = DEPRECATED_WARNING_PREFIX +
            'Setting dropdownAlign to "left" or "right" is deprecated, use "start" or "end" instead';
        if (align === 'left') {
            console.warn(deprecatedWarning);
            this._dropdownAlign = 'start';
        }
        else if (align === 'right') {
            console.warn(deprecatedWarning);
            this._dropdownAlign = 'end';
        }
        else {
            this._dropdownAlign = align;
        }
    }
    get dropdownAlign() {
        return this._dropdownAlign;
    }
    /**
     * Specifies whether the dropdown menu(s) should be appended to another container.
     * It will still be positioned the same.
     */
    dropdownContainer = '';
    /**
     * Specifies whether the direction should automatically be flipped if there is not enough space in the container or its parents.
     */
    dropdownResponsiveDirection = false;
    /**
     * Specifies whether the direction should automatically be flipped also according to the placement reference and its parents.
     * Only relevant when `dropdownResponsiveDirection` is set to `true`.
     */
    dropdownResponsiveDirectionToPlacement = true;
    _dropdownPlacement = '';
    /**
     * Specifies custom placement of the dropdown menu(s). This overrides the placement
     * specified by the direction. Can include `start`, `end`, `top` and `bottom`.
     * @defaultref {@link _dropdownPlacement}
     */
    set dropdownPlacement(placement) {
        this._dropdownPlacement = placement;
    }
    get dropdownPlacement() {
        return this._dropdownPlacement;
    }
    /**
     * Specifies what the placement of the dropdown menu(s) should be relative to.
     */
    dropdownPlacementReference = '';
    /**
     * Specifies the selector to determine which elements inside the dropdown menu(s) are dropdown items.
     * The default selector targets the bootstrap `dropdown-item` class.
     */
    dropdownItemSelector = '.dropdown-item';
    /**
     * Specifies the selector to determine which elements inside the dropdown menu(s) are not dropdown items.
     * The default selector excepts the `dropdown-item-no-link` class.
     */
    dropdownItemSelectorException = '.dropdown-item-no-link';
    /**
     * Focuses the first menu on open of the dropdown.
     */
    dropdownFocusOnOpen = true;
    /**
     * Focuses the first toggle on close of the dropdown.
     */
    dropdownFocusOnClose = true;
    /**
     * Closes the dropdown when the menu(s) are not visible anymore.
     */
    closeOnMenuScrollOut = true;
    /**
     * Closes the dropdown when the placement reference is not visible anymore.
     * Only works when either `placementReference` or `dropdownContainer` is set.
     */
    closeOnPlacementReferenceScrollOut = true;
    /**
     * Emits the current dropdown state whenever it is opened or closed.
     */
    dropdownOnToggle = new EventEmitter();
    /**
     * Emits when any dropdown item inside the dropdown menu(s) is selected.
     * The event is the element of the dropdown item.
     */
    dropdownOnSelect = new EventEmitter();
    /**
     * Emits when any the direction of the dropdown is changed, automatically or manually.
     * The event contains the new direction.
     */
    dropdownResponsiveDirectionChange = new EventEmitter();
    /** Whether the dropdown is actually open. */
    get isOpen() {
        return this.isOpenForConsumer;
    }
    /** Whether the dropdown appears open. */
    get appearsOpen() {
        return this._menus.every(menu => menu.content.classList.contains('show'));
    }
    /**
     * Whether the dropdown has previously been opened.
     */
    dropdownOpened = false;
    // the consumer facing state
    isOpenForConsumer = false;
    dropdownOpen = false;
    loaded = false;
    containerHostElement = null;
    stateChangeTimeout = 0;
    displayContainerComponent = false;
    containerComponentDisplayed = false;
    containerComponentRef;
    containerComponent;
    reopenAfterContainerTimeout = 0;
    changeDirectionTimeout = 0;
    closeDropdownTimeout = 0;
    _responsiveDirection = 'down';
    suppressOutsideClickInCurrentTick = false;
    resizeSubs;
    viewContainer = inject(ViewContainerRef);
    renderer = inject(Renderer2);
    resizeObserver = inject(ResizeObserverService);
    zone = inject(NgZone);
    cdRef = inject(ChangeDetectorRef);
    parent = inject(SiDropdownDirective, { optional: true, skipSelf: true });
    /**
     * @defaultref {@link _responsiveDirection}
     */
    set responsiveDirection(direction) {
        this._responsiveDirection = direction || 'down';
        this.cdRef.markForCheck();
        this.dropdownResponsiveDirectionChange.emit(this._responsiveDirection);
    }
    get responsiveDirection() {
        return this._responsiveDirection;
    }
    focusOnOpenTimeout = 0;
    hostElement = inject(ElementRef).nativeElement;
    _menus = [];
    get menus() {
        return this._menus;
    }
    _toggles = [];
    get toggles() {
        return this._toggles;
    }
    isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
    children = [];
    get isDropup() {
        return this.responsiveDirection === 'up';
    }
    get isDropstart() {
        return this.responsiveDirection === 'start';
    }
    get isDropend() {
        return this.responsiveDirection === 'end';
    }
    get dropdownShowClass() {
        return this.dropdownOpen;
    }
    ngOnInit() {
        this.parent?.addChild(this);
        this.updateInputs();
    }
    ngOnChanges() {
        this.updateInputs();
    }
    ngOnDestroy() {
        clearTimeout(this.changeDirectionTimeout);
        clearTimeout(this.stateChangeTimeout);
        clearTimeout(this.reopenAfterContainerTimeout);
        clearTimeout(this.focusOnOpenTimeout);
        clearTimeout(this.closeDropdownTimeout);
        this.parent?.removeChild(this);
        this.removeContainer();
        if (this.isBrowser) {
            removeEventListener('click', this.onClickOut);
            this.zone.runOutsideAngular(() => {
                removeEventListener('resize', this.onPlacementReferenceScroll);
                if (this.containerComponentDisplayed) {
                    removeEventListener('scroll', this.onPlacementReferenceScroll, true);
                }
            });
        }
        this.resizeSubs?.unsubscribe();
    }
    updateInputs() {
        let close = false;
        if (!this.loaded) {
            this.loaded = true;
            if ((this.dropdownOpenOnLoad || this.dropdownIsOpen) && !this.dropdownIsDisabled) {
                this.open();
            }
        }
        else {
            if (this.dropdownOpen && this.dropdownIsDisabled) {
                this.close();
                close = true;
            }
            else if (!this.dropdownOpen && this.dropdownIsOpen) {
                this.open();
            }
        }
        this.responsiveDirection = this.dropdownDirection;
        this.resetContainerAndPlacement(close);
    }
    getPlacementReferenceElement() {
        return resolveReference(this.hostElement, this.dropdownPlacementReference) ?? this.hostElement;
    }
    resetContainerAndPlacement(close = false) {
        const wasOpen = this.dropdownOpen;
        if (wasOpen) {
            this.closeDropdown();
        }
        this.displayContainerComponent = false;
        const containerHost = resolveReference(this.hostElement, this.dropdownContainer);
        if (containerHost) {
            this.containerHostElement = containerHost;
            this.displayContainerComponent = true;
        }
        else if (this.dropdownResponsiveDirection ||
            this.dropdownPlacement ||
            this.dropdownPlacementReference ||
            this.dropdownAlign === 'end' ||
            this.dropdownAlign === 'center') {
            this.containerHostElement = this.hostElement;
            this.displayContainerComponent = true;
        }
        else {
            this.containerHostElement = null;
        }
        if (wasOpen && !close) {
            this.reopenAfterContainerTimeout = setTimeout(() => this.attemptDropdownOpen());
        }
    }
    responsivelyChangeDirection(placementReferenceElement, isScrolling = false) {
        if (this.dropdownResponsiveDirection && this.containerComponentDisplayed && this.dropdownOpen) {
            const { responsiveDirection, close } = responsivelyCheckDirection({
                isScrolling,
                currentDirection: this.dropdownDirection,
                contentElements: this._menus.map(menu => menu.content),
                hostElement: this.containerHostElement,
                placement: this.dropdownPlacement,
                placementReferenceElement,
                align: this.dropdownAlign,
                responsiveDirectionToPlacement: this.dropdownResponsiveDirectionToPlacement,
                closeOnPlacementReferenceScrollOut: this.closeOnPlacementReferenceScrollOut,
                closeOnContentScrollOut: this.closeOnMenuScrollOut
            });
            if (responsiveDirection && responsiveDirection !== this.responsiveDirection) {
                // wont emit but value is there for position update; emit in zone
                this._responsiveDirection = responsiveDirection;
                this.zone.run(() => {
                    if (close) {
                        this.closeDropdown();
                    }
                    this.responsiveDirection = responsiveDirection;
                });
            }
            else if (close) {
                this.zone.run(() => this.closeDropdown());
            }
        }
    }
    addMenu(menu) {
        this._menus.push(menu);
        if (this.containerComponentDisplayed) {
            this.containerComponent.removeMenus();
            this.containerComponent.addMenus();
        }
        menu.updateState(this.dropdownOpen, this.getActualAlign());
    }
    removeMenu(menu) {
        const menuIndex = this._menus.indexOf(menu);
        if (menuIndex >= 0) {
            this._menus.splice(menuIndex, 1);
            if (this.containerComponentDisplayed) {
                this.containerComponent.removeMenus();
                this.containerComponent.addMenus();
            }
        }
    }
    onPlacementReferenceScroll = () => this.placementReferenceScrollHandler();
    placementReferenceScrollHandler() {
        if (this.dropdownOpen && this.containerComponentDisplayed) {
            const placementReferenceElement = this.getPlacementReferenceElement();
            this.responsivelyChangeDirection(placementReferenceElement, true);
            const position = getContentPositionString({
                contentElement: this.containerComponent.content,
                direction: this.responsiveDirection,
                placement: this.dropdownPlacement,
                placementReferenceElement,
                align: this.dropdownAlign
            });
            if (position !== this.containerComponent.elementTransform) {
                this.zone.run(() => {
                    this.containerComponent.elementTransform = position;
                    this.cdRef.markForCheck();
                });
            }
        }
    }
    addContainer() {
        if (!this.containerComponentDisplayed) {
            this.containerComponentRef = this.viewContainer.createComponent(SiDropdownContainerComponent);
            this.containerComponent = this.containerComponentRef.instance;
            this.containerComponent.setParent(this);
            this.renderer.appendChild(this.containerHostElement, this.containerComponent.content);
            this.containerComponentDisplayed = true;
            // Set a property of the HTML element for others to follow the chain without having to use the service.
            this.hostElement.dropdownChildren = this.containerComponent.content.children;
        }
    }
    removeContainer() {
        if (this.containerComponentDisplayed) {
            if (this.containerHostElement.contains(this.containerComponent.content)) {
                this.renderer.removeChild(this.containerHostElement, this.containerComponent.content);
            }
            this.containerComponentRef.destroy();
            this.containerComponentDisplayed = false;
            delete this.hostElement.dropdownChildren;
        }
    }
    onClickOut = (event) => this.clickOutHandler(event);
    clickOutHandler(event) {
        const target = event.target;
        if (!this.suppressOutsideClickInCurrentTick &&
            this.dropdownOpen &&
            this.dropdownCloseOnClickOut &&
            (!this.isInsideOverlay(target) || this.isInsideOverlay(this.hostElement)) &&
            !this.isElementOfAnyDropdownInHierarchy(target)) {
            this.closeDropdown();
        }
    }
    onKeydown(event) {
        const target = event.target;
        if (this.dropdownCloseOnEsc && !this.isElementOfAnyChildDropdownInHierarchy(target)) {
            this.closeDropdown();
        }
    }
    /** Toggles the dropdown. */
    toggle() {
        if (!this.dropdownIsDisabled) {
            if (this.dropdownOpen) {
                this.closeDropdown();
            }
            else {
                this.attemptDropdownOpen();
            }
        }
    }
    /** Opens the dropdown. */
    open() {
        if (!this.dropdownIsDisabled) {
            this.attemptDropdownOpen();
        }
    }
    /** Closes the dropdown. */
    close() {
        this.closeDropdown();
    }
    addToggle(toggle) {
        this._toggles.push(toggle);
    }
    removeToggle(toggle) {
        this._toggles.splice(this._toggles.indexOf(toggle));
    }
    addChild(child) {
        this.children.push(child);
    }
    removeChild(child) {
        this.children.splice(this.children.indexOf(child));
    }
    waitForVisibilityChange() {
        if (this.containerHostElement &&
            (this.containerHostElement.offsetParent ||
                (window.getComputedStyle(this.containerHostElement).position === 'fixed' &&
                    window.getComputedStyle(this.containerHostElement).display !== 'none') ||
                this.containerHostElement === document.body ||
                this.containerHostElement === document.documentElement)) {
            this.resizeSubs?.unsubscribe();
            this.openDropdown();
        }
    }
    attemptDropdownOpen() {
        if (this.containerHostElement &&
            this.containerHostElement !== document.body &&
            this.containerHostElement !== document.documentElement &&
            !(this.containerHostElement.offsetParent ||
                window.getComputedStyle(this.containerHostElement).position === 'fixed' ||
                window.getComputedStyle(this.containerHostElement).display !== 'none')) {
            this.resizeSubs?.unsubscribe();
            this.resizeSubs = this.resizeObserver
                .observe(this.containerHostElement, RESIZE_THROTTLE_TIMEOUT, true, true)
                .subscribe(() => this.waitForVisibilityChange());
        }
        else {
            this.openDropdown();
        }
    }
    openDropdown() {
        this.suppressOutsideClickInCurrentTick = true;
        setTimeout(() => (this.suppressOutsideClickInCurrentTick = false));
        if (!this.dropdownOpen) {
            if (this.displayContainerComponent) {
                this.addContainer();
                if (this.dropdownResponsiveDirection) {
                    this.containerComponent.hide = true;
                }
            }
            this.responsiveDirection = this.dropdownDirection;
            clearTimeout(this.closeDropdownTimeout);
            this.dropdownOpen = true;
            this.dropdownOpened = true;
            if (this.displayContainerComponent) {
                setTimeout(() => {
                    this.isOpenForConsumer = true;
                    this.dropdownOnToggle.emit(this.isOpenForConsumer);
                });
            }
            else {
                this.isOpenForConsumer = true;
                this.dropdownOnToggle.emit(this.isOpenForConsumer);
            }
            setTimeout(() => addEventListener('click', this.onClickOut));
            this._toggles.forEach(dropdownToggle => dropdownToggle.updateState(true));
            const align = this.getActualAlign();
            this._menus.forEach(dropdownMenu => dropdownMenu.updateState(true, align));
            if (this.containerComponentDisplayed) {
                this.zone.runOutsideAngular(() => {
                    // Add event listener to window as capturing to handle tested element scrolling
                    addEventListener('scroll', this.onPlacementReferenceScroll, true);
                    // here we _actually_ want window.resize, nothing else since this is about a fixed position
                    addEventListener('resize', this.onPlacementReferenceScroll);
                });
                this.containerComponent.elementTransform = getContentPositionString({
                    contentElement: this.containerComponent.content,
                    direction: this.responsiveDirection,
                    placement: this.dropdownPlacement,
                    placementReferenceElement: this.getPlacementReferenceElement(),
                    align: this.dropdownAlign
                });
            }
            this.changeDirectionTimeout = setTimeout(() => {
                this.changeDirectionTimeout = 0;
                const placementReferenceElement = this.getPlacementReferenceElement();
                this.responsivelyChangeDirection(placementReferenceElement);
                if (this.containerComponentDisplayed) {
                    this.containerComponent.elementTransform = getContentPositionString({
                        contentElement: this.containerComponent.content,
                        direction: this.responsiveDirection,
                        placement: this.dropdownPlacement,
                        placementReferenceElement,
                        align: this.dropdownAlign
                    });
                }
                if (this.containerComponent) {
                    this.containerComponent.hide = false;
                    this.cdRef.detectChanges();
                }
            });
            if (this.dropdownFocusOnOpen) {
                this.focusOnOpenTimeout = window.setTimeout(() => {
                    this.focusOnOpenTimeout = 0;
                    if (this._menus.length) {
                        this._menus[0].content.focus();
                    }
                });
            }
        }
    }
    closeDropdown() {
        if (this.dropdownOpen) {
            this.responsiveDirection = this.dropdownDirection;
            clearTimeout(this.closeDropdownTimeout);
            this.dropdownOpen = false;
            this.closeDropdownTimeout = setTimeout(() => {
                this.isOpenForConsumer = false;
                this.dropdownOnToggle.emit(this.isOpenForConsumer);
            });
            removeEventListener('click', this.onClickOut);
            this._toggles.forEach(dropdownToggle => dropdownToggle.updateState(false));
            this._menus.forEach(dropdownMenu => dropdownMenu.updateState(false));
            if (this.containerComponentDisplayed) {
                this.zone.runOutsideAngular(() => {
                    removeEventListener('scroll', this.onPlacementReferenceScroll, true);
                    removeEventListener('resize', this.onPlacementReferenceScroll);
                });
            }
            if (this.displayContainerComponent) {
                this.removeContainer();
            }
            if (this.dropdownFocusOnClose && this._toggles.length) {
                this._toggles[0].content.focus();
            }
            this.children.forEach(child => child.closeDropdown());
            this.cdRef.markForCheck();
        }
    }
    getActualAlign() {
        return this.responsiveDirection === 'start' || this.responsiveDirection === 'end'
            ? 'none'
            : this.dropdownAlign;
    }
    /** Selects a dropdown item. */
    selectItem(item) {
        this.dropdownOnSelect.emit(item);
        if (this.isElementOfDropdown(item) &&
            !this.isDropdownToggleForDirectChild(item) &&
            !this.isElementOfAnyChildDropdownInHierarchy(item) &&
            this.dropdownCloseOnSelect) {
            this.close();
        }
    }
    isInsideOverlay(item) {
        let currentItem = item;
        while (currentItem) {
            if (currentItem.classList.contains('cdk-overlay-container')) {
                return true;
            }
            currentItem = currentItem.parentElement;
        }
        return false;
    }
    isElementOfAnyDropdownInHierarchy(item) {
        return this.isElementOfDropdown(item) || this.isElementOfAnyChildDropdownInHierarchy(item);
    }
    isElementOfAnyChildDropdownInHierarchy(item) {
        return this.children.some(child => child.isElementOfAnyDropdownInHierarchy(item));
    }
    isElementOfDropdown(item) {
        return this._menus.some(menu => menu.content.contains(item));
    }
    isDropdownToggleForDirectChild(item) {
        return this.children.some(child => child.toggles.some(toggle => toggle.content.contains(item)));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiDropdownDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiDropdownDirective, selector: "[siDropdown]", inputs: { dropdownIsDisabled: "dropdownIsDisabled", dropdownIsOpen: "dropdownIsOpen", dropdownOpenOnLoad: "dropdownOpenOnLoad", dropdownCloseOnSelect: "dropdownCloseOnSelect", dropdownCloseOnClickOut: "dropdownCloseOnClickOut", dropdownCloseOnEsc: "dropdownCloseOnEsc", dropdownDirection: "dropdownDirection", dropdownAlign: "dropdownAlign", dropdownContainer: "dropdownContainer", dropdownResponsiveDirection: "dropdownResponsiveDirection", dropdownResponsiveDirectionToPlacement: "dropdownResponsiveDirectionToPlacement", dropdownPlacement: "dropdownPlacement", dropdownPlacementReference: "dropdownPlacementReference", dropdownItemSelector: "dropdownItemSelector", dropdownItemSelectorException: "dropdownItemSelectorException", dropdownFocusOnOpen: "dropdownFocusOnOpen", dropdownFocusOnClose: "dropdownFocusOnClose", closeOnMenuScrollOut: "closeOnMenuScrollOut", closeOnPlacementReferenceScrollOut: "closeOnPlacementReferenceScrollOut" }, outputs: { dropdownOnToggle: "dropdownOnToggle", dropdownOnSelect: "dropdownOnSelect", dropdownResponsiveDirectionChange: "dropdownResponsiveDirectionChange" }, host: { listeners: { "keydown.escape": "onKeydown($event)" }, properties: { "class.dropup": "this.isDropup", "class.dropstart": "this.isDropstart", "class.dropend": "this.isDropend", "class.show": "this.dropdownShowClass" }, classAttribute: "dropdown" }, exportAs: ["si-dropdown"], usesOnChanges: true, ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiDropdownDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siDropdown]',
                    exportAs: 'si-dropdown',
                    host: {
                        class: 'dropdown'
                    }
                }]
        }], propDecorators: { dropdownIsDisabled: [{
                type: Input
            }], dropdownIsOpen: [{
                type: Input
            }], dropdownOpenOnLoad: [{
                type: Input
            }], dropdownCloseOnSelect: [{
                type: Input
            }], dropdownCloseOnClickOut: [{
                type: Input
            }], dropdownCloseOnEsc: [{
                type: Input
            }], dropdownDirection: [{
                type: Input
            }], dropdownAlign: [{
                type: Input
            }], dropdownContainer: [{
                type: Input
            }], dropdownResponsiveDirection: [{
                type: Input
            }], dropdownResponsiveDirectionToPlacement: [{
                type: Input
            }], dropdownPlacement: [{
                type: Input
            }], dropdownPlacementReference: [{
                type: Input
            }], dropdownItemSelector: [{
                type: Input
            }], dropdownItemSelectorException: [{
                type: Input
            }], dropdownFocusOnOpen: [{
                type: Input
            }], dropdownFocusOnClose: [{
                type: Input
            }], closeOnMenuScrollOut: [{
                type: Input
            }], closeOnPlacementReferenceScrollOut: [{
                type: Input
            }], dropdownOnToggle: [{
                type: Output
            }], dropdownOnSelect: [{
                type: Output
            }], dropdownResponsiveDirectionChange: [{
                type: Output
            }], isDropup: [{
                type: HostBinding,
                args: ['class.dropup']
            }], isDropstart: [{
                type: HostBinding,
                args: ['class.dropstart']
            }], isDropend: [{
                type: HostBinding,
                args: ['class.dropend']
            }], dropdownShowClass: [{
                type: HostBinding,
                args: ['class.show']
            }], onKeydown: [{
                type: HostListener,
                args: ['keydown.escape', ['$event']]
            }] } });

class SiDropdownMenuDirective {
    content = inject(ElementRef).nativeElement;
    parentElement;
    siDropdownDirective = inject(SiDropdownDirective);
    /**
     * Whether the dropdown has previously been opened.
     */
    dropdownOpened = false;
    dropdownOpen = false;
    alignStart = true;
    alignEnd = false;
    alignCenter = false;
    tabindex = -1;
    ngOnInit() {
        this.siDropdownDirective.addMenu(this);
        this.parentElement = this.content.parentElement;
    }
    ngOnDestroy() {
        this.siDropdownDirective.removeMenu(this);
        clearTimeout(this.keyboardTimeout);
    }
    updateState(state, align = '') {
        this.alignCenter = align === 'center';
        this.alignStart = !align || align === 'start';
        this.alignEnd = align === 'end';
        this.dropdownOpen = state;
        if (state) {
            this.dropdownOpened = true;
        }
    }
    keyboardTimeout;
    onClickOrKeydown(event, force = false) {
        let currentTarget = event.target;
        const tag = currentTarget.tagName.toLowerCase();
        // Wait for the click caused by the enter on <button> and <a> with href, in order to prevent timing issues.
        if (!force &&
            event instanceof KeyboardEvent &&
            (tag === 'button' || (tag === 'a' && currentTarget.href !== undefined))) {
            clearTimeout(this.keyboardTimeout);
            this.keyboardTimeout = setTimeout(() => {
                this.onClickOrKeydown(event, true);
            });
            return;
        }
        clearTimeout(this.keyboardTimeout);
        const itemSelector = this.siDropdownDirective.dropdownItemSelector;
        const itemSelectorException = this.siDropdownDirective.dropdownItemSelectorException;
        while (currentTarget &&
            currentTarget !== this.content &&
            (!currentTarget.matches(itemSelector) || currentTarget.matches(itemSelectorException))) {
            currentTarget = currentTarget.parentElement;
        }
        if (currentTarget &&
            currentTarget !== this.content &&
            currentTarget.matches(itemSelector) &&
            !currentTarget.matches(itemSelectorException)) {
            if (!currentTarget.classList.contains('disabled')) {
                this.siDropdownDirective.selectItem(currentTarget);
            }
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiDropdownMenuDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiDropdownMenuDirective, selector: "[siDropdownMenu]", inputs: { tabindex: "tabindex" }, host: { listeners: { "click": "onClickOrKeydown($event)", "keydown.enter": "onClickOrKeydown($event)" }, properties: { "class.show": "this.dropdownOpen", "class.align-start": "this.alignStart", "class.align-end": "this.alignEnd", "class.align-center": "this.alignCenter", "tabindex": "this.tabindex" }, classAttribute: "dropdown-menu" }, exportAs: ["si-dropdown-menu"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiDropdownMenuDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siDropdownMenu]',
                    exportAs: 'si-dropdown-menu',
                    host: {
                        class: 'dropdown-menu'
                    }
                }]
        }], propDecorators: { dropdownOpen: [{
                type: HostBinding,
                args: ['class.show']
            }], alignStart: [{
                type: HostBinding,
                args: ['class.align-start']
            }], alignEnd: [{
                type: HostBinding,
                args: ['class.align-end']
            }], alignCenter: [{
                type: HostBinding,
                args: ['class.align-center']
            }], tabindex: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['tabindex']
            }], onClickOrKeydown: [{
                type: HostListener,
                args: ['click', ['$event']]
            }, {
                type: HostListener,
                args: ['keydown.enter', ['$event']]
            }] } });

class SiDropdownToggleDirective {
    content = inject(ElementRef).nativeElement;
    siDropdownDirective = inject(SiDropdownDirective);
    toggleExecutedInTick = false;
    ngAfterViewInit() {
        this.setA11y(false);
    }
    dropdownOpen = false;
    ngOnDestroy() {
        this.siDropdownDirective.removeToggle(this);
    }
    ngOnInit() {
        this.siDropdownDirective.addToggle(this);
    }
    updateState(state) {
        this.dropdownOpen = state;
        this.setA11y(state);
    }
    setA11y(state) {
        const menuItem = this.content.querySelector('[role=menuitem]');
        if (menuItem) {
            menuItem.setAttribute('aria-expanded', `${state}`);
            menuItem.setAttribute('aria-haspopup', 'true');
        }
        else {
            this.content.setAttribute('aria-expanded', `${state}`);
            this.content.setAttribute('aria-haspopup', 'true');
        }
    }
    open() {
        this.siDropdownDirective.open();
    }
    close() {
        this.siDropdownDirective.close();
    }
    onClick() {
        // when toggle is used on a button, enter also fires a click event --> suppressing it.
        if (!this.toggleExecutedInTick) {
            this.siDropdownDirective.toggle();
            this.toggleExecutedInTick = true;
            setTimeout(() => (this.toggleExecutedInTick = false));
        }
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiDropdownToggleDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
    static ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "16.1.5", type: SiDropdownToggleDirective, selector: "[siDropdownToggle]", host: { listeners: { "click": "onClick()", "keydown.enter": "onClick()" }, properties: { "class.show": "this.dropdownOpen" } }, exportAs: ["si-dropdown-toggle"], ngImport: i0 });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiDropdownToggleDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[siDropdownToggle]',
                    exportAs: 'si-dropdown-toggle'
                }]
        }], propDecorators: { dropdownOpen: [{
                type: HostBinding,
                args: ['class.show']
            }], onClick: [{
                type: HostListener,
                args: ['click']
            }, {
                type: HostListener,
                args: ['keydown.enter']
            }] } });

class SiDropdownModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiDropdownModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.1.5", ngImport: i0, type: SiDropdownModule, declarations: [SiDropdownContainerComponent,
            SiDropdownDirective,
            SiDropdownMenuDirective,
            SiDropdownToggleDirective], imports: [CommonModule], exports: [SiDropdownDirective, SiDropdownMenuDirective, SiDropdownToggleDirective] });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiDropdownModule, imports: [CommonModule] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.1.5", ngImport: i0, type: SiDropdownModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [
                        SiDropdownContainerComponent,
                        SiDropdownDirective,
                        SiDropdownMenuDirective,
                        SiDropdownToggleDirective
                    ],
                    exports: [SiDropdownDirective, SiDropdownMenuDirective, SiDropdownToggleDirective]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { SiDropdownContainerComponent, SiDropdownDirective, SiDropdownMenuDirective, SiDropdownModule, SiDropdownToggleDirective };
